import React, { useCallback, useMemo } from 'react';
import { Alert, AlertProps, Box, Collapse, ListItem, UnorderedList } from '@televet/kibble-ui';
import { setConfigurationStatus, WritebackConfigurationStatus } from '../../state/paymentSettingsSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';

const WritebackConfigurationAlert = (): JSX.Element | null => {
  const { primaryIntegrationName } = useIntegrationsProvider();
  const dispatch = useAppDispatch();
  const status = useAppSelector((state) => state.paymentSettings.configurationStatus);
  const incompleteFields = useAppSelector((state) => state.paymentSettings.incompleteFields);

  const WritebackAlertStatusMap: Record<WritebackConfigurationStatus, AlertProps> = {
    [WritebackConfigurationStatus.Incomplete]: {
      status: 'warning',
      title: 'Configuration Incomplete',
      description: `The following fields need to be completed before Flow can write payments to ${primaryIntegrationName}:`,
    },
    [WritebackConfigurationStatus.Complete]: {
      status: 'success',
      title: 'Configuration Complete',
      description: `Payments taken in Flow will now successfully write back to ${primaryIntegrationName}.`,
    },
    [WritebackConfigurationStatus.None]: { status: 'info', title: '', description: '' },
  };

  const alertChildren = useMemo(() => {
    return (
      incompleteFields && (
        <UnorderedList mt={-2}>
          {incompleteFields.map((item, index) => {
            return (
              <ListItem key={`alert-list-item-${index}`} fontSize="sm">
                {item}
              </ListItem>
            );
          })}
        </UnorderedList>
      )
    );
  }, [incompleteFields]);

  const handleAlertClose = useCallback(() => {
    dispatch(setConfigurationStatus(WritebackConfigurationStatus.None));
  }, [dispatch]);

  return (
    <Collapse in={status !== WritebackConfigurationStatus.None}>
      <Box pb={3}>
        <Alert
          status={WritebackAlertStatusMap[status].status}
          title={WritebackAlertStatusMap[status].title}
          titleProps={{ children: null, size: 'xs' }}
          descriptionProps={{ size: 'sm', children: WritebackAlertStatusMap[status].description }}
          hideCloseButton={!(status === WritebackConfigurationStatus.Complete)}
          onClose={handleAlertClose}
        >
          {alertChildren}
        </Alert>
      </Box>
    </Collapse>
  );
};

export default WritebackConfigurationAlert;
